<template>
  <fieldset class="confirm-screen pt-10 pl-30 pr-30">
    <div v-if="isPartnerDashboard"  class="row" >
      <div class="col-md-12">
        <h2>CONGRATULATIONS!</h2>
        <h3 class="mb-0">
          Member added successfully!
        </h3>
        <br>
      </div>
    </div>


    <div v-if="!isPartnerDashboard" class="row"  >
      <div class="col-md-12">
        <h2>CONGRATULATIONS!</h2>
        <h3 class="mb-0">
          {{ congratulationText }}
        </h3>
        <br>
        <h3 class="mb-0">
          The user can now order Rides, Meals, Groceries and have Medication delivered by calling;
        </h3>
        <br>
        <h4 class="mt-10 mb-0">
          <b class="phone-number">1-855-464-6872</b>
          for Rides and Medication Delivery
        </h4>
        <h4 class="mb-0">
          <b class="phone-number">1-855-754-5328</b> for Meals
        </h4>
        <h4 class="mb-0">
          <b class="phone-number">1-855-222-4919</b> for Groceries
        </h4>
      </div>
    </div>

    <div v-if="!isPartnerDashboard" class="row"  >
      <div class="col-md-12">
        <div class="action-instruction">
          <b>Press 1</b> to immediately request an <b>Uber</b> or <b>Lyft</b> to be sent to your home. No need to speak with an operator.
        </div>
        <div class="action-instruction">
          <b>Press 2</b> to request an <b>Uber</b> or <b>Lyft</b> to be sent to where we dropped you off last.
        </div>
        <div class="action-instruction">
          <b>Press 3, 4, or 5</b> to request an <b>Uber</b> or <b>Lyft</b> to a custom pickup location (once you're registered please give
          us a call to set these up.
        </div>
        <div class="action-instruction">
          <b>Press 6</b> to book on-demand rides to new places using voice commands.
        </div>
        <div class="action-instruction">
          <b>Press 7</b> to request meals delivered from local restaurants near you, fulfilled by delivery services like <b>DoorDash</b> and
          more.
        </div>
        <div class="action-instruction">
          <b>Press 8</b> request groceries delivered from local supermarkets, fulfilled by services like <b>Instacart, Walmart</b> and more.
        </div>
        <div class="action-instruction">
          <b>Press 0</b> to speak with an operator about <b>medication management, scheduling requests in advance, home services</b> or
          anything else.
        </div>
      </div>
    </div>

    <div
      v-if="referralCode && !isPartnerDashboard"
      class="row"
    >
      <div class="col-md-12">
        <h5 v-if="!partnerCode">
          Their referral code is:
          <span class="referral-code">{{ referralCode }}</span>
        </h5>
        <h5>($10 for anyone you refer and you get $10 after they take their first ride!)</h5>
      </div>
    </div>

    <div v-if="!isPartnerDashboard" class="form-submit-button-group mt-10"  >
      <p-button
        v-if="!partnerCode"
        id="btnConfirm"
        round
        type="purple"
        @click="updateCustomerFlag"
      >
        {{ flagged === 0 ? 'Flag Account' : 'Unflag Account' }}
      </p-button>

      <p-button
        id="btnBack"
        round
        type="purple"
        @click="initRegistrationData"
      >
        Back To Step 1
      </p-button>
      <p-button
        round
        type="warning"
        @click="orderRide"
      >
        Order a ride
      </p-button>
    </div>
  </fieldset>
</template>

<script lang="ts" setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { ElLoading } from 'element-plus';
import events from '@/events';
import delay from 'yoctodelay';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { useRoute, useRouter } from 'vue-router';
import { callerService } from '@/services/caller';

const props = defineProps({
  partnerCode: {
    type: String,
    default: '',
  },

  isNewUser: {
    type: Boolean,
    default: true,
  },
});

const router = useRouter();
const route = useRoute();
const { isPartnerDashboard } = useDashboard();

const emit = defineEmits(['init-registration-data']);

const { showErrorPopupDialog } = useSwalDialog();
const { newCustomerInfo } = useCustomer();

const flagged = ref(newCustomerInfo.value.flagged);

const referralCode = computed(() => newCustomerInfo.value.referralCode);

const congratulationText = computed(() =>
  props.isNewUser ? 'You have signed up a new GoGo Member!' : 'You have completed the registration of an existing member!',
);

const updateCustomerFlag = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Updating Customer...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await callerService.updateCaller({
      callerId: newCustomerInfo.value.id,
      data: {
        flagged: flagged.value === 0 ? 1 : 0,
      },
    });

    flagged.value = flagged.value === 0 ? 1 : 0;
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  loading.close();
};

const initRegistrationData = () => {
  emit('init-registration-data');
  events.emit('reset-registration-data');
};

const orderRide = async () => {
  events.emit('refresh-right-panel', {
    phone: newCustomerInfo.value.phoneNumber,
    isAllowRedirection: false,
  });

  await delay(100);

  router.push({
    path: '/operator/ride-ordering',
    query: {
      ...route.query,
      fromRegistration: 'true',
      phoneNumber: newCustomerInfo.value.phoneNumber,
    },
  });
};

onBeforeUnmount(() => {
  initRegistrationData();
});
</script>

<style lang="scss">
.confirm-screen {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  @media (max-width: 576px) {
    max-width: unset;
    padding: 10px 20px 0;
  }

  h2 {
    font-size: 26px;
    font-weight: bold;
    text-align: center;
    margin: 20px 0 25px;
  }

  h3 {
    font-size: 22px;
    font-weight: bold;
    @media (max-width: 576px) {
      font-size: 20px;
      padding-bottom: 5px;
    }
  }

  h4 {
    margin-top: 0;
    font-size: 18px;
    color: #000 !important;
  }

  h5 {
    margin-bottom: 0;
    font-size: 20px;
    font-weight: bold;

    &:last-child {
      margin-bottom: 40px;
    }

    .referral-code {
      color: #ef8157;
      font-family: sans-serif;
    }
  }

  .action-instruction {
    font-size: 18px;
  }

  .warning {
    color: red;
  }
}
</style>
